// src/modules/app-views/serviceOrders/pages/Document/actions/floatButton.js
// *******************************************************************************
// SERVICE ORDERS FLOAT BUTTON ACTIONS
// *******************************************************************************
// Float button actions for service order document operations.
// Actions visibility depends on document type (budget/order) and current status.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import {
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SwapOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  CarryOutOutlined,
  GiftOutlined,
} from '@ant-design/icons';

// *******************************************************************************
// FLOAT BUTTON ACTIONS
// *******************************************************************************

/**
 * Build float button actions array for service order document
 * @param {Object} context - Context with data and handlers
 * @param {Object} context.data - Service order document data
 * @param {Object} context.handlers - Action handlers
 * @returns {Array} Array of float button action configurations
 */
const serviceOrdersDocumentFloatButton = (context = {}) => {
  const { data, handlers = {} } = context;

  // ─────────────────────────────────────────────────────────────────────────────
  // STATUS CHECKS
  // ─────────────────────────────────────────────────────────────────────────────
  const isNew = !data?._id || data?._id === 'add';
  if (isNew) return [];

  const status = data?.status;
  const type = data?.type;
  const isBudget = type === 'budget';
  const isOrder = type === 'order';

  // ─────────────────────────────────────────────────────────────────────────────
  // BUILD ACTIONS ARRAY
  // ─────────────────────────────────────────────────────────────────────────────
  return [
    // BUDGET: Draft -> Send to Customer
    isBudget &&
      status === 'draft' && {
        key: 'sendToCustomer',
        icon: <SendOutlined />,
        tooltipText: 'send_to_customer',
        type: 'primary',
        onClick: handlers.onSendToCustomer,
      },

    // BUDGET: Sent -> Mark Approved
    isBudget &&
      status === 'sent' && {
        key: 'markApproved',
        icon: <CheckCircleOutlined />,
        tooltipText: 'mark_approved',
        type: 'primary',
        onClick: handlers.onMarkApproved,
      },

    // BUDGET: Sent -> Mark Rejected
    isBudget &&
      status === 'sent' && {
        key: 'markRejected',
        icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
        tooltipText: 'mark_rejected',
        onClick: handlers.onMarkRejected,
      },

    // BUDGET: Approved -> Convert to Order
    isBudget &&
      status === 'approved' && {
        key: 'convertToOrder',
        icon: <SwapOutlined />,
        tooltipText: 'convert_to_order',
        type: 'primary',
        onClick: handlers.onConvertToOrder,
      },

    // ORDER: Open -> Start Work
    isOrder &&
      status === 'open' && {
        key: 'startWork',
        icon: <PlayCircleOutlined />,
        tooltipText: 'start_work',
        type: 'primary',
        onClick: handlers.onStartWork,
      },

    // ORDER: In Progress -> Mark Completed
    isOrder &&
      status === 'in_progress' && {
        key: 'markCompleted',
        icon: <CarryOutOutlined />,
        tooltipText: 'mark_completed',
        type: 'primary',
        onClick: handlers.onMarkCompleted,
      },

    // ORDER: In Progress -> Waiting Parts
    isOrder &&
      status === 'in_progress' && {
        key: 'waitingParts',
        icon: <PauseCircleOutlined />,
        tooltipText: 'waiting_parts',
        onClick: handlers.onWaitingParts,
      },

    // ORDER: Waiting Parts -> Resume Work
    isOrder &&
      status === 'waiting_parts' && {
        key: 'resumeWork',
        icon: <PlayCircleOutlined />,
        tooltipText: 'resume_work',
        type: 'primary',
        onClick: handlers.onResumeWork,
      },

    // ORDER: Completed -> Mark Delivered
    isOrder &&
      status === 'completed' && {
        key: 'markDelivered',
        icon: <GiftOutlined />,
        tooltipText: 'mark_delivered',
        type: 'primary',
        onClick: handlers.onMarkDelivered,
      },
  ].filter(Boolean);
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default serviceOrdersDocumentFloatButton;
